import { useEffect, useState } from "react";
import {
  useConnector,
  useHits,
  useRefinementList,
  useSearchBox,
} from "react-instantsearch-hooks-web";
import "@/utils/firebase-init";
import connectRange from "instantsearch.js/es/connectors/range/connectRange";
import connectToggleRefinement from "instantsearch.js/es/connectors/toggle-refinement/connectToggleRefinement";
import { connectPagination } from "instantsearch.js/es/connectors";
import { useRouter } from "next/router";
import * as colleges from "@/utils/data/colleges-data";
import { useDevice } from "@/contexts/DeviceContext";
import { makePlural } from "@/utils/string";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  useToken,
  chakra,
  Icon,
  Collapse,
  Checkbox,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from "@chakra-ui/icons";
import { ProfileTable } from "./ProfileTable";
import { FaFilter, FaMinus, FaPlus } from "react-icons/fa";
import { DATABASE_SIZE } from "@/utils/profile";
import { onSearch } from "@/utils/analytics";

const CUSTOM_CHAKRA_SELECT_COMPONENTS = { DropdownIndicator: () => null };

export const searchStateToURL = (state) => {
  if (!state || !state.admityogi_users) {
    return "";
  }

  let url = "?";
  if (window.location.href.includes("advanced=true")) {
    url += "advanced=true&";
  }
  const { refinementList, query, page, toggle } = state.admityogi_users;
  if (refinementList) {
    for (const [key, value] of Object.entries(refinementList)) {
      if (value.length > 0) {
        url += `${key}=${encodeURIComponent(value.join(","))}&`;
      }
    }
  }
  url += `query=${encodeURIComponent(query ?? "")}&`;
  if (page) {
    url += `page=${encodeURIComponent(page)}&`;
  }
  if (toggle) {
    for (const [key, value] of Object.entries(toggle)) {
      if (value) {
        url += `${key}=${encodeURIComponent(value)}&`;
      }
    }
  }
  if (url.endsWith("&")) {
    url = url.slice(0, -1);
  }
  if (url === "?") {
    return "";
  }
  return url;
};

const NResults = () => {
  const { results } = useHits();
  const { nbHits } = results;
  if (nbHits > 150)
    return (
      <b>
        {DATABASE_SIZE.applications}+ applications ({DATABASE_SIZE.essays}+ essays)
      </b>
    );
  else
    return (
      <b>
        {nbHits} {makePlural("profile", "s", nbHits)}
      </b>
    );
};

export const Pagination = (props) => {
  const connector = useConnector(connectPagination, props);
  const { currentRefinement, nbPages, isFirstPage, isLastPage, refine } = connector;
  const onNavigate = (direction) => refine(currentRefinement + direction);
  const showNResults = props.showNResults ?? true;

  if (nbPages === 0 || nbPages === 1) {
    return <Box textAlign="right">{showNResults && <NResults />}</Box>;
  }

  return (
    <Box textAlign="right">
      {showNResults && <NResults />}
      {showNResults && " - "}
      Page {currentRefinement + 1} of {nbPages}
      {"  "}
      <ButtonGroup mb={2} ml={2}>
        <IconButton
          icon={<ChevronLeftIcon />}
          variant="outline"
          colorScheme="black"
          isDisabled={isFirstPage}
          size="sm"
          onClick={() => onNavigate(-1)}
        />
        <IconButton
          icon={<ChevronRightIcon />}
          variant="outline"
          colorScheme="black"
          isDisabled={isLastPage}
          size="sm"
          onClick={() => onNavigate(1)}
        />
      </ButtonGroup>
    </Box>
  );
};

export const FilterSection = ({ children }) => {
  const { isMobile } = useDevice();
  const [isOpen, setIsOpen] = useState(!isMobile);

  return (
    <>
      <Box display={{ md: "none" }}>
        <Button
          onClick={() => setIsOpen(!isOpen)}
          colorScheme="purple"
          leftIcon={<Icon as={FaFilter} />}
          w="100%"
          textAlign="left"
        >
          {isOpen ? "Hide filters" : "Show filters"}
        </Button>
      </Box>
      <Collapse in={isOpen} style={{ overflow: "visible" }}>
        {children}
      </Collapse>
    </>
  );
};

export const CollapsibleFilter = ({ header, children, color, borderColor = "black", w }) => {
  const resolvedColor = useToken("colors", color);
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Box w={w}>
      <Box
        border="0.125rem solid black"
        py={1}
        px={3}
        bg="white"
        borderRadius={8}
        borderColor={borderColor}
        w={w ?? "fit-content"}
        shadow={`-0.3rem 0.3rem ${resolvedColor}`}
      >
        <chakra.h3 fontFamily="heading" cursor="pointer" onClick={() => setIsOpen((i) => !i)}>
          <motion.span animate={{ rotate: "45deg", color: isOpen ? "#444" : "#000000" }}>
            <Icon as={isOpen ? FaMinus : FaPlus} boxSize={4} />
          </motion.span>
          <chakra.span ml={2}>{header}</chakra.span>
        </chakra.h3>
      </Box>
      <Collapse in={isOpen} style={{ overflow: "visible" }}>
        <Box mt={4}>{children}</Box>
      </Collapse>
    </Box>
  );
};

export function Hits(props) {
  const { hits } = useHits(props);

  useEffect(() => {
    onSearch();
  }, [hits]);

  return <ProfileTable profiles={hits} />;
}

export function SearchBox(props) {
  const router = useRouter();
  const { query, refine, ...rest } = useSearchBox(props);
  const [queryShown, setQueryShown] = useState(query);
  const guide = props.placeholder;

  const onChange = (e) => {
    refine(e.target.value);
    setQueryShown(e.target.value);
    if (props.onChange) props.onChange(e.target.value);
  };

  useEffect(() => {
    const value = router.query.query;
    if (!!value) {
      onChange({ target: { value } });
    }
  }, [router.query]);

  return (
    <chakra.div mb={4}>
      <chakra.h3 fontFamily="heading">{props.header}</chakra.h3>
      <InputGroup size="lg" mt={4}>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input onChange={onChange} value={queryShown} type="text" placeholder={guide} />
      </InputGroup>
    </chakra.div>
  );
}

export function AlwaysOnToggle(props) {
  const { refine } = useConnector(connectToggleRefinement, props);

  useEffect(() => {
    if ("value" in props) {
      if (props.value === true || props.value === false) refine(!props.value);
    } else {
      refine(true);
    }
  }, []);

  return <></>;
}

export function Toggle(props) {
  const { currentRefinement, value, refine, ...rest } = useConnector(
    connectToggleRefinement,
    props,
  );
  const [isLoading, setLoading] = useState(false);
  const router = useRouter();

  const onClick = () => {
    if (value.isRefined) {
      const params = router.query;
      params[props.attribute] = "false";
      // convert to a URL string
      const url = new URLSearchParams(params).toString();
      window.location.href = router.pathname + "?" + url;
    } else {
      refine();
    }
  };

  useEffect(() => {
    const value = router.query[props.attribute] === "true";
    if (value) {
      refine();
    }
  }, [router.query]);

  if (value.count === 0) {
    return null;
  } else {
    if (props.as === "check") {
      return (
        <Checkbox
          onChange={onClick}
          isLoading={isLoading}
          isChecked={value.isRefined}
          mr={props.margin ? 1 : 0}
          mt={props.margin ? 1 : 0}
        >
          {props.title}
        </Checkbox>
      );
    } else {
      return (
        <Button
          onClick={onClick}
          isLoading={isLoading}
          variant={value.isRefined ? "solid" : "outline"}
          colorScheme={value.isRefined ? "red" : "gray"}
          mr={props.margin ? 1 : 0}
          mt={props.margin ? 1 : 0}
          size="sm"
        >
          {props.title}
        </Button>
      );
    }
  }
}

export function RangeSelector(props) {
  const { refine } = useConnector(connectRange, props);
  const [selectedRange, setSelectedRange] = useState([props.min, props.max]);

  const onChange = (val) => setSelectedRange(val);
  const onChangeEnd = (val) => refine(val);

  return (
    <>
      <chakra.p mt={1} mb={0}>
        {props.title}
        <chakra.span float="right">
          {selectedRange[0]}-{selectedRange[1]}
        </chakra.span>
      </chakra.p>
      <RangeSlider
        defaultValue={[props.min, props.max]}
        min={props.min}
        max={props.max}
        step={props.precision ?? 1}
        onChange={onChange}
        onChangeEnd={onChangeEnd}
      >
        <RangeSliderTrack>
          <RangeSliderFilledTrack />
        </RangeSliderTrack>
        <RangeSliderThumb boxSize={6} index={0} />
        <RangeSliderThumb boxSize={6} index={1} />
      </RangeSlider>
    </>
  );
}

const RefinementListButton = ({ item, onClick, ...rest }) => {
  const [isLoading, setLoading] = useState(false);

  const onClickButton = () => {
    onClick();
    setLoading(true);
  };

  useEffect(() => setLoading(false), [item.isRefined]);

  return (
    <Button
      key={item.value}
      onClick={onClickButton}
      isLoading={isLoading}
      variant={item.isRefined ? "solid" : "outline"}
      colorScheme={item.isRefined ? "red" : "gray"}
      size="sm"
      {...rest}
    >
      {item.value}
    </Button>
  );
};

export function RefinementList(props) {
  const { items, refine } = useRefinementList(props);
  const router = useRouter();

  if (props.sorted == "numeric") {
    items.sort((a, b) => parseInt(a.value) - parseInt(b.value));
  }

  useEffect(() => {
    const values = router.query[props.attribute]?.split(",") ?? [];
    if (values.length > 0) {
      values.forEach(refine);
    }
  }, [router.query]);

  return items.map((item) => (
    <RefinementListButton
      key={item.value}
      onClick={() => refine(item.value)}
      item={item}
      mr={props.margin ? 1 : 0}
      mt={props.margin ? 1 : 0}
    />
  ));
}

export function SearchableRefinementList(props) {
  const { items, refine } = useRefinementList(props);
  const router = useRouter();
  const [oldVal, setOldVal] = useState([]);

  useEffect(() => {
    const queryValues = router.query[props.attribute]?.split(",") ?? [];
    if (queryValues.length > 0) {
      onChange(queryValues.map((value) => ({ value, label: value })));
    }
  }, [router.query]);

  const onChange = (newVal) => {
    newVal = newVal.map((item) => item.value);
    // Find elements that have changed, and toggle them
    for (const elem of new Set([...oldVal, ...newVal])) {
      if (!oldVal.includes(elem) || !newVal.includes(elem)) {
        refine(elem);
      }
    }
    setOldVal(newVal);
  };

  let selectOptions = items;
  if (props.sorted === "college") {
    selectOptions.sort((a, b) => colleges.find(a.value).rank - colleges.find(b.value).rank);
  } else if (props.sorted === "alpha") {
    selectOptions.sort((a, b) => a.value.localeCompare(b.value));
  } else if (props.sorted === "count") {
    selectOptions.sort((a, b) => b.count - a.count);
  }

  selectOptions = selectOptions.map((item) => ({
    value: item.value,
    label: item.value,
  }));

  return (
    <Select
      size="sm"
      components={CUSTOM_CHAKRA_SELECT_COMPONENTS}
      isMulti
      placeholder={props.title}
      options={selectOptions}
      value={selectOptions.filter((option) => oldVal.includes(option.value))}
      variant={props.selectVariant ?? "unstyled"}
      onChange={onChange}
    />
  );
}
